import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { uniqueId } from "lodash";

const CollapseContext = createContext({ collapseId: null });

// This is a collapse container that's built to manage and make accessible the
// <CollapseSwitch> and <CollapseContent>, by centralizing their shared context state.
//
// Example :
//
//   <Collapse>
//     <CollapseSwitch>Click me to toggle</CollapseSwitch>
//     <CollapseContent>I'm only shown when expanded</CollapseContent>
//   </Collapse>
//
// For a real world example, see the <MapSidebarEventsList> component that makes use of
// this component, and synchronizes the state of multiple collapsed through the Redux
// store.
//
const Collapse = ({ expanded = false, ...props }) => {
  const { children, onExpandedChanged, shouldCollapse } = props;

  const collapseIdRef = useRef(uniqueId("collapse"));
  const collapseId = collapseIdRef.current;

  const [isExpanded, setIsExpanded] = useState(expanded);

  const handleExpandedChanged = useCallback(
    willExpand => {
      setIsExpanded(willExpand);
      // If a callback is provided for the expanded state change, call it when the state
      // is updated.
      if (onExpandedChanged) onExpandedChanged(willExpand);
    },
    [onExpandedChanged]
  );

  useEffect(() => {
    // If the `shouldCollapse` callback prop is provided, it's called on each render and
    // can make the container collapse from an external update change. This is useful to
    // make multiple collapses act like accordions, and handle the case where one
    // collapse is open, and the other shoud collapse at the same time.
    //
    // If the `shouldCollapse` is a `useCallback` wrapped function (which is the preferred
    // way of using this prop, and should always be the case), it's updated in the parent
    // container when one of its dependencies change, and automatically re-renders the
    // Collapse component, forcing it to take into account the `shouldCollapse` return
    // value change.
    //
    // Note that if the `shouldCollapse` callback is not memoized through a `useCallback`
    // hook, it will be run at every render of the component, which may lead into
    // unexpected behavior.
    if (shouldCollapse && shouldCollapse()) handleExpandedChanged(false);
  }, [shouldCollapse, handleExpandedChanged]);

  const contextValue = {
    collapseId,
    isExpanded,
    setIsExpanded: handleExpandedChanged
  };

  return (
    <CollapseContext.Provider value={contextValue}>{children}</CollapseContext.Provider>
  );
};

export const useCollapseContext = () => useContext(CollapseContext) || {};

export default Collapse;
